import "./Performances.scss";
import React, { useEffect, useMemo, useState } from "react";
import { capitalize, snakeCase } from "lodash-es";
import useTrend from "../../Trends/useTrend";
import {
  Loader,
  Tooltip,
  useIcon,
  normalizeNumbers,
  formatNumber,
} from "@SeemplicityDev/shared";
import { timeScales } from "../../../DashboardUtils";
import WidgetError from "../../WidgetError/WidgetError";
import type { SelectedScope } from "../../../../types/scope";

const DIRECTION = {
  UP: "Up",
  DOWN: "Down",
  EQ: "No Change",
} as const;

type Direction = (typeof DIRECTION)[keyof typeof DIRECTION];

// Each trend entry is a [label, value] tuple as returned by the `trends` query.
type TrendEntry = [string, number];

export interface PerformancesProps {
  timeScale: string;
  filterToExecute?: string | null;
  selectedScope?: SelectedScope | null;
  scopeToExecute?: string | null;
  widgetKey?: string;
  setIsReadyToExport?: (isReady: boolean) => void;
  isForExport?: boolean;
}

const Performances: React.FC<PerformancesProps> = ({
  timeScale,
  filterToExecute,
  selectedScope,
  scopeToExecute,
  widgetKey,
  setIsReadyToExport,
  isForExport,
}) => {
  const { getSvg } = useIcon(isForExport ? "LIGHT" : undefined);
  const [numOfFixedFindings, setNumOfFixedFindings] = useState(0);
  const [lastPeriodFixedFindings, setLastPeriodFixedFindings] = useState(0);
  const [direction, setDirection] = useState<Direction | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [noData, setNoData] = useState(false);

  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      selection: ["closed_today"],
      timescale: timeScale,
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: ["actual_status"],
      },
      aggregate_functions: ["SUM"],
      round_functions: ["ROUND"],
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  useEffect(() => {
    if (!!trendDataError) {
      setIsLoading(false);
      setIsReadyToExport?.(true);
    }
  }, [trendDataError]);

  useEffect(() => {
    const trendEntries = data as TrendEntry[] | null | undefined;
    if (trendEntries?.length) {
      setNoData(false);
      const trendsData = trendEntries?.reverse();
      const fixedFindings = trendsData?.[0]?.[1];
      setNumOfFixedFindings(fixedFindings || 0);

      const lastPeriodFixedFindings = trendsData?.[1]?.[1];
      setLastPeriodFixedFindings(lastPeriodFixedFindings || 0);

      const direction =
        fixedFindings === lastPeriodFixedFindings
          ? DIRECTION.EQ
          : fixedFindings > lastPeriodFixedFindings
            ? DIRECTION.UP
            : DIRECTION.DOWN;
      setDirection(direction);
      setIsLoading(false);
      setIsReadyToExport?.(true);
    } else {
      setNoData(true);
      setIsLoading(false);
      setIsReadyToExport?.(true);
    }
  }, [data]);

  useEffect(() => {
    if (trendLoading) {
      setIsLoading(true);
      setIsReadyToExport?.(false);
    }
  }, [trendLoading]);

  const timeScaleText = useMemo(() => {
    return timeScale !== timeScales.DAY
      ? `Last ${capitalize(timeScale)}`
      : "Yesterday";
  }, [timeScale]);

  return (
    <div className={`performances-widget-wrapper ${noData ? "no-data" : ""}`}>
      {trendDataError ? (
        <WidgetError onWidgetsReload={refetchTrend} />
      ) : isLoading ? (
        <Loader center img={getSvg("icon-spinner")} iconSize={"xxxxl"} />
      ) : noData ? (
        <Loader
          icon={"seem-icon-manualfinding"}
          center
          className={"empty-state-no-match-to-filter"}
          subtitle={"No match for the selected filter"}
        />
      ) : (
        <>
          {numOfFixedFindings === 0 ? (
            <Loader
              data-testid={"chart-no-data"}
              className={"empty-state-no-match-to-filter"}
              center
              icon={"seem-icon-manualfinding"}
              subtitle={"There are no Findings for this Scope and Filter"}
            />
          ) : (
            !!numOfFixedFindings && (
              <>
                <Tooltip
                  content={
                    direction === DIRECTION.EQ
                      ? `No change from ${timeScaleText}`
                      : `Compared to ${normalizeNumbers(
                          lastPeriodFixedFindings
                        )} that were fixed ${timeScaleText}`
                  }
                >
                  <div className="performance-data-row">
                    <div
                      data-testid={`fixed-findings`}
                      className={`performance-data`}
                    >{`${formatNumber(numOfFixedFindings)}`}</div>
                    {!!direction && direction !== DIRECTION.EQ && (
                      <i
                        data-testid="direction-icon"
                        className={`seem-icon seem-icon-performance-${direction?.toLowerCase()} performance-direction-icon performance-${snakeCase(
                          direction?.toLowerCase()
                        )}`}
                      />
                    )}
                  </div>
                </Tooltip>
                <div
                  className={`performance-icon-container ${
                    !numOfFixedFindings ? "is-empty" : ""
                  }`}
                >
                  <div className={`square-icon`} />
                  <span>{`Fixed Findings`}</span>
                </div>
              </>
            )
          )}
        </>
      )}
    </div>
  );
};

export default Performances;
